import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { FilterFieldMeta, FilterValue, ConfigurableField } from '@/types/filterBuilder';

interface FilterValueInputProps {
  field: FilterFieldMeta | ConfigurableField;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  compact?: boolean;
}

export function FilterValueInput({
  field,
  value,
  onChange,
  compact = false,
}: FilterValueInputProps) {
  const { inputType, options, placeholder } = field;

  switch (inputType) {
    case 'text':
      return (
        <Input
          type="text"
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={compact ? 'h-8 text-sm' : ''}
        />
      );

    case 'number':
      return (
        <Input
          type="number"
          value={(value as number) ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          placeholder={placeholder}
          className={compact ? 'h-8 text-sm w-24' : 'w-32'}
        />
      );

    case 'number-range': {
      const rangeValue = (value as { min?: number; max?: number }) || {};
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={rangeValue.min ?? ''}
            onChange={(e) =>
              onChange({
                ...rangeValue,
                min: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            placeholder="Min"
            className={compact ? 'h-8 text-sm w-20' : 'w-24'}
          />
          <span className="text-muted-foreground text-sm">to</span>
          <Input
            type="number"
            value={rangeValue.max ?? ''}
            onChange={(e) =>
              onChange({
                ...rangeValue,
                max: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            placeholder="Max"
            className={compact ? 'h-8 text-sm w-20' : 'w-24'}
          />
        </div>
      );
    }

    case 'date':
      return (
        <Input
          type="date"
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          className={compact ? 'h-8 text-sm' : ''}
        />
      );

    case 'date-range': {
      const rangeValue = (value as { start?: string; end?: string }) || {};
      return (
        <div className={compact ? 'flex flex-col gap-1.5 w-full' : 'flex items-center gap-2'}>
          <Input
            type="date"
            value={rangeValue.start || ''}
            onChange={(e) =>
              onChange({
                ...rangeValue,
                start: e.target.value || undefined,
              })
            }
            className={compact ? 'h-8 text-sm w-full' : ''}
          />
          <span className="text-muted-foreground text-sm text-center">to</span>
          <Input
            type="date"
            value={rangeValue.end || ''}
            onChange={(e) =>
              onChange({
                ...rangeValue,
                end: e.target.value || undefined,
              })
            }
            className={compact ? 'h-8 text-sm w-full' : ''}
          />
        </div>
      );
    }

    case 'boolean':
      return (
        <div className="flex items-center gap-2">
          <Checkbox
            checked={(value as boolean) || false}
            onCheckedChange={(checked) => onChange(checked === true)}
          />
          <Label className="text-sm text-muted-foreground">
            {(value as boolean) ? 'Yes' : 'No'}
          </Label>
        </div>
      );

    case 'select':
      return (
        <Select
          value={(value as string) || ''}
          onValueChange={(val) => onChange(val)}
        >
          <SelectTrigger className={compact ? 'h-8 text-sm' : ''}>
            <SelectValue placeholder={placeholder || 'Select...'} />
          </SelectTrigger>
          <SelectContent>
            {options?.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case 'multi-select': {
      const selectedValues = (value as string[]) || [];
      const selectedLabels = selectedValues
        .map((v) => options?.find((o) => o.value === v)?.label || v)
        .slice(0, 2);
      const moreCount = selectedValues.length - 2;

      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={`justify-start ${compact ? 'h-8 text-sm' : ''}`}
            >
              {selectedValues.length === 0 ? (
                <span className="text-muted-foreground">
                  {placeholder || 'Select...'}
                </span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {selectedLabels.map((label) => (
                    <Badge key={label} variant="secondary" className="text-xs">
                      {label}
                    </Badge>
                  ))}
                  {moreCount > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      +{moreCount}
                    </Badge>
                  )}
                </div>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            <div className="space-y-2">
              {options?.map((opt) => (
                <div key={opt.value} className="flex items-center gap-2">
                  <Checkbox
                    id={`${field.key}-${opt.value}`}
                    checked={selectedValues.includes(opt.value)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        onChange([...selectedValues, opt.value]);
                      } else {
                        onChange(selectedValues.filter((v) => v !== opt.value));
                      }
                    }}
                  />
                  <Label
                    htmlFor={`${field.key}-${opt.value}`}
                    className="text-sm cursor-pointer"
                  >
                    {opt.label}
                  </Label>
                </div>
              ))}
            </div>
            {selectedValues.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-2"
                onClick={() => onChange([])}
              >
                Clear all
              </Button>
            )}
          </PopoverContent>
        </Popover>
      );
    }

    default:
      return null;
  }
}

// Display-only component to show the current value
export function FilterValueDisplay({
  field,
  value,
}: {
  field: FilterFieldMeta;
  value: FilterValue;
}) {
  const { inputType, options } = field;

  if (value === null || value === undefined) {
    return <span className="text-muted-foreground text-sm">Not set</span>;
  }

  switch (inputType) {
    case 'text':
    case 'number':
    case 'date':
      return <span className="text-sm">{String(value)}</span>;

    case 'number-range': {
      const range = value as { min?: number; max?: number };
      if (range.min !== undefined && range.max !== undefined) {
        return <span className="text-sm">{range.min} - {range.max}</span>;
      }
      if (range.min !== undefined) {
        return <span className="text-sm">{'>='} {range.min}</span>;
      }
      if (range.max !== undefined) {
        return <span className="text-sm">{'<='} {range.max}</span>;
      }
      return null;
    }

    case 'date-range': {
      const range = value as { start?: string; end?: string };
      if (range.start && range.end) {
        return <span className="text-sm">{range.start} to {range.end}</span>;
      }
      if (range.start) {
        return <span className="text-sm">From {range.start}</span>;
      }
      if (range.end) {
        return <span className="text-sm">Until {range.end}</span>;
      }
      return null;
    }

    case 'boolean':
      return <span className="text-sm">{value ? 'Yes' : 'No'}</span>;

    case 'select': {
      const opt = options?.find((o) => o.value === value);
      return <span className="text-sm">{opt?.label || String(value)}</span>;
    }

    case 'multi-select': {
      const selected = value as string[];
      return (
        <div className="flex flex-wrap gap-1">
          {selected.map((v) => {
            const opt = options?.find((o) => o.value === v);
            return (
              <Badge key={v} variant="secondary" className="text-xs">
                {opt?.label || v}
              </Badge>
            );
          })}
        </div>
      );
    }

    default:
      return null;
  }
}

export default FilterValueInput;
